import { useState } from 'react'
import {
  useListParticipantsQuery,
  type ListParticipantsApiArg,
  type ListParticipantsResponse,
  type ParticipantSummary,
} from '@proaxiv/server-api'

/** Sort direction accepted by the `listParticipants` `order` query param. */
export type ParticipantSortOrder = 'asc' | 'desc'

type ParticipantStatusFilter = ListParticipantsApiArg['status']

/** Options passed to {@link usePagedParticipants} to drive one participant table's query. */
export interface UsePagedParticipantsOptions {
  eventId: string
  /** Status filter for this table; omit to list every status (not used by any current tab). */
  status?: ParticipantStatusFilter
  /** Initial sort field — must be one of the backend's whitelisted sort fields. */
  defaultSort: string
  defaultOrder?: ParticipantSortOrder
  /** Rows per page. Defaults to 25; the backend caps this at 50. */
  defaultTake?: number
}

/** Paging/sorting state and setters returned by {@link usePagedParticipants}. */
export interface UsePagedParticipantsResult {
  participants: ParticipantSummary[]
  total: number
  isLoading: boolean
  isError: boolean
  offset: number
  take: number
  sort: string
  order: ParticipantSortOrder
  /** Jump to an arbitrary offset (clamped to [0, last valid page start]). */
  setOffset: (offset: number) => void
  /** Change page size; resets to the first page. */
  setTake: (take: number) => void
  /** Click a sortable column header: toggles asc/desc if already sorted by this field,
   *  otherwise switches to this field ascending. Always resets to the first page. */
  toggleSort: (field: string) => void
}

/**
 * Drives one Event Management participants table's paging + sorting against the
 * offset/take/sort/order `listParticipants` contract (see GitHub issue #260 — "Table
 * Pagination"). Each table (Invited, Registered, Rejected, Accepted, Unattended, Attended) must
 * call this independently — instances never share `offset`/`take`/`sort`/`order`, so paging one
 * table never affects another's page or sort order, even on the same tab (e.g. the Registrations
 * tab's pending + rejected tables).
 */
export function usePagedParticipants({
  eventId,
  status,
  defaultSort,
  defaultOrder = 'asc',
  defaultTake = 25,
}: UsePagedParticipantsOptions): UsePagedParticipantsResult {
  const [offset, setOffsetState] = useState(0)
  const [take, setTakeState] = useState(defaultTake)
  const [sort, setSort] = useState(defaultSort)
  const [order, setOrder] = useState<ParticipantSortOrder>(defaultOrder)

  const { data, isLoading, isError } = useListParticipantsQuery({
    eventId,
    status,
    offset,
    take,
    sort,
    order,
  })

  const participants = (data as ListParticipantsResponse | undefined)?.participants ?? []
  const total = (data as ListParticipantsResponse | undefined)?.total ?? 0

  function setOffset(next: number) {
    const lastPageStart = total > 0 ? Math.floor((total - 1) / take) * take : 0
    setOffsetState(Math.max(0, Math.min(next, lastPageStart)))
  }

  function setTake(next: number) {
    setTakeState(next)
    setOffsetState(0)
  }

  function toggleSort(field: string) {
    if (field === sort) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSort(field)
      setOrder('asc')
    }
    setOffsetState(0)
  }

  return {
    participants,
    total,
    isLoading,
    isError,
    offset,
    take,
    sort,
    order,
    setOffset,
    setTake,
    toggleSort,
  }
}
